import { AdmRuleHistory, CreateLazyLoadingRule, LazyLoadingRule, UpdateLazyLoadingRule } from '@pubstack/common/src/adm/rules'
import { sortByName } from '@pubstack/common/src/sort'
import { MutationToasts, useMutation, useQuery } from '~/api/api-client'
import { buildUrlWithSearchParams } from '~/utils/url'

export const LAZY_LOADING_RULES_QUERY_KEYS = ['adm', 'lazy-loading-rules']
const url = '/adm/lazy-loading-rules'
//-------------------------------------------------------------------------
// Queries
//-------------------------------------------------------------------------
/** `archived: true` asks for the set-aside rules instead of the live ones. Owner-only at the API. */
export const useLazyLoadingRules = (withStacksUse = false, archived = false) => {
  const urlWithSearchParams = buildUrlWithSearchParams(url, { ...(withStacksUse ? { withStacksUse } : {}), ...(archived ? { archived } : {}) })
  const query = useQuery<LazyLoadingRule[]>({
    url: urlWithSearchParams,
    keys: [...LAZY_LOADING_RULES_QUERY_KEYS, { withStacksUse, archived }],
    select: sortByName,
  })
  return { ...query, data: query.data || [] }
}

export const useLazyLoadingRule = (id: string, enabled: boolean = !!id) => {
  return useQuery<LazyLoadingRule>({
    url: `${url}/${id}`,
    keys: [...LAZY_LOADING_RULES_QUERY_KEYS, id],
    enabled,
  })
}

export const useLazyLoadingRulesWithHistory = () => {
  return useQuery<AdmRuleHistory>({
    url: `${url}/history`,
    keys: [...LAZY_LOADING_RULES_QUERY_KEYS, 'history'],
  })
}

//-------------------------------------------------------------------------
// Mutations
//-------------------------------------------------------------------------
export const useCreateLazyLoadingRule = (toasts: MutationToasts = {}) => {
  return useMutation<CreateLazyLoadingRule, LazyLoadingRule>({
    method: 'post',
    url,
    collectionKeys: LAZY_LOADING_RULES_QUERY_KEYS,
    ...toasts,
  })
}

export const useUpdateLazyLoadingRule = (toasts: MutationToasts = {}) => {
  return useMutation<UpdateLazyLoadingRule, LazyLoadingRule>({
    method: 'put',
    url,
    collectionKeys: LAZY_LOADING_RULES_QUERY_KEYS,
    ...toasts,
  })
}

/**
 * Set a rule aside, or bring it back. Owner-only at the API, and refused while any stack still uses it.
 *
 * `collectionKeys` covers the live list and the archived one alike — they share a prefix — so a rule
 * leaving one and appearing in the other refetches both.
 */
export const useArchiveLazyLoadingRule = (toasts: MutationToasts = {}) => {
  return useMutation<{ id: string; archived: boolean }, LazyLoadingRule>({
    method: 'put',
    url: ({ id }) => `${url}/${id}/archive`,
    collectionKeys: LAZY_LOADING_RULES_QUERY_KEYS,
    ...toasts,
  })
}
